import { useEffect, useState, useRef } from "react";
import DashboardLayout from "components/app/DashboardLayout";
import Button from "components/form/Button";
import axios from "utils/axios";
import "react-loading-skeleton/dist/skeleton.css";
import {
  SimpleTextInput,
  SelectInput,
  InputDate,
  TimeInput,
  RadioLink,
  MultipleLinkInput,
  TextareaInput,
} from "components/form/FormOeuvre";
import classNames from "classnames";
import ErrorMessage from "components/form/ErrorMessage";
import SuccessMessage from "components/form/SuccessMessage";
import Loader from "components/ui/Loader";
import {
  optionLevel,
  type,
  optionMethod,
  optionOriginal,
} from "constants/data-filter";
import { API_ENDPOINTS } from "utils/api-endpoints";
import { getImageByLink } from "utils/link";

const ItemTab = ({ item, onClick }) => {
  return (
    <tr className="border-b bg-white">
      <td className="py-4 px-6 text-sm">{item.title.substr(0, 16)}..</td>
      <td className="py-4 px-6 text-sm">{item.typeOfCard}</td>
      <td className="py-4 px-6 text-sm">{item.publisher}</td>
      <td className="py-4 px-6 text-sm">{item.typeChoosen}</td>
      <td className="py-4 px-6 text-sm">{item.dateOfComposition}</td>
      <td className="py-4 px-6 text-sm capitalize">
        {item.originalTranscription}
      </td>
      <td className="flex justify-start py-3 pr-6 text-xs">
        {item.status === "Pending" && (
          <span className="text-xs px-2 py-1 font-semibold leading-tight text-yellow-700 bg-yellow-100 rounded-full capitalize">
            {item.status}
          </span>
        )}
        {item.status === "Accepted" && (
          <span className="text-xs px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full capitalize">
            {item.status}
          </span>
        )}
        {item.status === "Refused" && (
          <span className="text-xs px-2 py-1 font-semibold leading-tight text-pink-700 bg-pink-100 rounded-full capitalize">
            {item.status}
          </span>
        )}
      </td>
      <td className="py-4 px-6 text-sm capitalize">
        <button className="" onClick={onClick}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-4 text-pblue h-4"
          >
            <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
            <path
              fillRule="evenodd"
              d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </td>
    </tr>
  );
};

const BlockLink = ({ title, content }) => {
  return (
    <div className="flex flex-col justify-between overflow-hidden text-left transition-shadow duration-200 bg-white rounded shadow-xl group hover:shadow-2xl">
      <div className="p-5">
        <p className="mb-2 text-sm font-medium border-b pb-1.5">{title}</p>
        <p className="text-sm leading-5 text-gray-900">
          <ul className="list-disc ml-4 space-y-4">
            {Array.isArray(content) &&
              content.map((item, index) => (
                <li key={index} className="border-b pb-2">
                  <a
                    className="text-indigo-600 hover:text-pblue cursor-pointer"
                    href={`${"/oppdb/redirect/?"}${"&redirect_url="}${item}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img
                      alt={`${getImageByLink(item)}`}
                      src={`${"/oppdb/images/assets/logos/"}${getImageByLink(
                        item
                      )}${".png"}`}
                      className="h-8"
                    />
                  </a>
                </li>
              ))}
          </ul>
        </p>
      </div>
      <div className="w-full h-1 ml-auto duration-300 origin-left transform scale-x-0 bg-deep-purple-accent-400 group-hover:scale-x-100" />
    </div>
  );
};

const Block = ({ title, content }) => {
  return (
    <div className="flex flex-col justify-between overflow-hidden text-left transition-shadow duration-200 bg-white rounded shadow-xl group hover:shadow-2xl">
      <div className="p-5">
        <p className="mb-2 text-sm font-medium border-b pb-1.5">{title}</p>
        <p className="text-sm leading-5 text-gray-900">{content}</p>
      </div>
      <div className="w-full h-1 ml-auto duration-300 origin-left transform scale-x-0 bg-deep-purple-accent-400 group-hover:scale-x-100" />
    </div>
  );
};

const Status = ({ isOpen, onClick }) => {
  return (
    <>
      {isOpen ? (
        <span
          onClick={onClick}
          className="text-gray-400 bg-gray-200 rounded-full"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M18 12H6"
            />
          </svg>
        </span>
      ) : (
        <span
          onClick={onClick}
          className="text-white bg-yellowgray rounded-full"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
        </span>
      )}
    </>
  );
};

const links = ["https://www.rungispianopiano-festival.com"];

function Work() {
  const selectMethod = useRef(null);
  const selectType = useRef(null);
  const selectLevel = useRef(null);
  const selectComposer = useRef(null);
  const selectOriginal = useRef(null);

  // Help state
  const [fieldHelp, setFieldHelp] = useState("");

  // User infos
  const [created, setCreated] = useState(true),
    [oeuvres, setOeuvres] = useState([]),
    [optionComposer, setOptionComposer] = useState([]),
    [dataOeuvre, setDataOeuvre] = useState({
      greatRepertoire: null,
      title: null,
      publisher: null,
      typeChoosen: null,
      dateOfPublication: null,
      publisherRef: null,
      arrangeurName: null,
      content: null,
      level: null,
      composer_id: null,
      collectionName: null,
      mouvement: null,
      dateOfComposition: null,
      originalTranscription: null,
      transcriptorName: null,
      instrument: null,
      duration: null,
      firstPerformance: null,
      nomenclature: null,
      linkToScores: [],
      linkToHears: [],
      linkToBuys: [],
      perfomedAtRppf: null,
      notes: null,
    });

  // handling statelinkToHear
  const [isLoading, setIsLoading] = useState(false),
    [errors, setErrorsMessage] = useState([]),
    [success, setSuccessMessage] = useState(""),
    [isSubmitted, setIsSubmitted] = useState(false),
    [showDetail, setShowDetail] = useState(false),
    [data, setDataToShow] = useState([]),
    [isOpen, setIsOpen] = useState(1);

  const hanleSHowDetails = (id) => {
    setShowDetail(true);
    const dataClick = oeuvres.filter((res) => res.id === id);
    console.log("dataClick", dataClick[0]);
    setDataToShow(dataClick[0]);
  };

  useEffect(() => {
    if (!showDetail) {
      setDataToShow([]);
    }
  }, [showDetail]);

  const getUserOeuvre = async () => {
    await axios
      .get(API_ENDPOINTS.GET_WORK)
      .then((res) => {
        const { data } = res.data;
        console.log("data", data);
        setOeuvres(data);
      })
      .catch(() => {
        //console.log("error", err.response)
      });
  };
  const getAllComposer = async () => {
    await axios
      .get(API_ENDPOINTS.GET_ALL_COMPOSERS)
      .then((res) => {
        const { data } = res.data;
        setOptionComposer(data);
      })
      .catch((err) => {
        //console.log("error composer", err.response)
      });
  };

  useEffect(() => {
    getAllComposer();
    getUserOeuvre();
  }, []);

  // Submit oeuvre forms
  const submitFormOeuvre = () => {
    setIsSubmitted(true);

    setSuccessMessage(null);

    setErrorsMessage([]);

    setIsLoading(true);

    axios
      .post(API_ENDPOINTS.ADD_WORK, dataOeuvre)
      .then((res) => {
        if (res.status === 200) {
          setIsLoading(false);

          setIsSubmitted(false);

          setSuccessMessage("Created successfuly !");

          setDataOeuvre({
            title: "",
            publisher: "",
            dateOfPublication: "",
            publisherRef: "",
            arrangeurName: "",
            content: "",
            collectionName: "",
            mouvement: "",
            dateOfComposition: "",
            transcriptorName: "",
            instrument: "",
            duration: "",
            firstPerformance: "",
            nomenclature: "",
            linkToScores: [],
            linkToHears: [],
            linkToBuys: [],
            perfomedAtRppf: "",
            notes: "",
          });

          selectMethod?.current.clearValue();
          selectType?.current.clearValue();
          selectLevel?.current.clearValue();
          // selectOriginal?.current.clearValue();
          // selectComposer?.current.clearValue();

          setTimeout(() => {
            setCreated(true);
            setSuccessMessage(null);
            getUserOeuvre();
          }, 1000);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.log("error oeuvres", error.response);
        setErrorsMessage(Object.values(error.response.data.errors).flat());

        /*if(err.response){
        setErrorsMessage(" Something went wrong. Please some fields are required ")
      }*/
      });
  };

  const displayOeuvreForUser = (data) => {
    return (
      <>
        {Array.isArray(data) &&
          data.map((item) => (
            <ItemTab item={item} onClick={() => hanleSHowDetails(item.id)} />
          ))}
      </>
    );
  };

  const addNewLinkToScore = (event) => {
    if (event.key === "Enter" && event.target.value !== "") {
      setDataOeuvre({
        ...dataOeuvre,
        linkToScores: [...dataOeuvre.linkToScores, event.target.value],
      });
      event.target.value = "";
    }
  };

  const addNewLinkToHear = (event) => {
    if (event.key === "Enter" && event.target.value !== "") {
      setDataOeuvre({
        ...dataOeuvre,
        linkToHears: [...dataOeuvre.linkToHears, event.target.value],
      });
      event.target.value = "";
    }
  };

  const addNewLinkToBuys = (event) => {
    if (event.key === "Enter" && event.target.value !== "") {
      setDataOeuvre({
        ...dataOeuvre,
        linkToBuys: [...dataOeuvre.linkToBuys, event.target.value],
      });
      event.target.value = "";
    }
  };

  const handleSetIsOpen = (id) => {
    if (isOpen === id) {
      setIsOpen(0);
    } else {
      setIsOpen(id);
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
            {!created ? "Create card" : "Cards submitted"}
          </h2>
        </div>
        <div className="flex-shrink-0">
          {!showDetail && (
            <Button onClick={() => setCreated(!created)}>
              {!created ? "Cards submitted" : "Create card"}
            </Button>
          )}

          {showDetail && (
            <Button
              onClick={() => {
                setShowDetail(false);
                setCreated(created);
              }}
            >
              Cards submitted
            </Button>
          )}
        </div>
      </div>
      <div className="w-full flex flex-col">
        {showDetail && (
          <div className="w-full flex flex-col mb-16">
            <div className="mt-0 space-y-8">
              <div className="border-2 border-gray-100 rounded-lg shadow-md hover:shadow-xl">
                <button
                  onClick={() => handleSetIsOpen(1)}
                  className="flex items-center justify-between w-full p-8"
                >
                  <h1 className="font-semibold text-gray-700 dark:text-white">
                    WORK INFORMATION
                  </h1>

                  <Status
                    onClick={() => handleSetIsOpen(1)}
                    isOpen={isOpen === 1 ? true : false}
                  />
                </button>

                <hr className="border-gray-200" />

                <div
                  id="block_1"
                  className={`${
                    isOpen === 1 ? "  animate-fade-in-down block " : "  hidden "
                  }${" relative grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 p-4 bg-gray-100"}`}
                >
                  <Block title={"Title"} content={data?.title} />
                  <Block title={"Type of card"} content={data?.typeOfCard} />
                  <Block title={"Type"} content={data?.typeChoosen} />
                  <Block title={"Level"} content={data?.level} />
                  <Block
                    title={"Date of publication"}
                    content={data?.dateOfPublication}
                  />
                  <Block
                    title={"Date of Composition"}
                    content={data?.dateOfComposition}
                  />
                  <Block title={"Content"} content={data?.mouvement} />
                  {/* {data?.instrument &&<Block title={"Instrument"} content={data?.instrument} />} */}
                  {data?.instrument && (
                    <Block title={"Instrument"} content={data?.instrument} />
                  )}
                  <Block title={"Duration "} content={data?.duration} />
                </div>
              </div>

              <div className="border-2 border-gray-100 rounded-lg shadow-md hover:shadow-xl">
                <button
                  onClick={() => handleSetIsOpen(2)}
                  className="flex items-center justify-between w-full p-8"
                >
                  <h1 className="font-semibold text-gray-700 dark:text-white uppercase">
                    COMPOSER{" "}
                  </h1>

                  <Status
                    onClick={() => handleSetIsOpen(2)}
                    isOpen={isOpen === 2 ? true : false}
                  />
                </button>

                <hr className="border-gray-200" />

                <div
                  id="block_2"
                  className={`${
                    isOpen === 2
                      ? " transition duration-500 ease-in-out block "
                      : " hidden "
                  }${" relative grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 p-4 bg-gray-100"}`}
                >
                  <Block title={"Composer name"} content={data?.composerName} />
                  <Block
                    title={"Arranger name"}
                    content={data?.arrangeurName}
                  />
                  <Block
                    title={"Original or transcription"}
                    content={data?.originalTranscription}
                  />
                  <Block
                    title={"Transcriptor name"}
                    content={data?.transcriptorName}
                  />
                  <Block title={"Publisher"} content={data?.publisher} />
                  <Block
                    title={"Publisher reference"}
                    content={data?.publisherRef}
                  />
                </div>
              </div>

              <div className="border-2 border-gray-100 rounded-lg shadow-md hover:shadow-xl">
                <button
                  onClick={() => handleSetIsOpen(3)}
                  className="animate-shine flex items-center justify-between w-full p-8"
                >
                  <h1 className="font-semibold text-gray-700 dark:text-white">
                    ABOUT LINKS{" "}
                  </h1>

                  <Status
                    onClick={() => handleSetIsOpen(3)}
                    isOpen={isOpen === 3 ? true : false}
                  />
                </button>

                <hr className="border-gray-200" />

                <div
                  id="block_3"
                  className={`${
                    isOpen === 3 ? " block " : " hidden "
                  }${" relative grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 p-4 bg-gray-100"}`}
                >
                  <BlockLink
                    title={"Link to score "}
                    content={data?.linkToScores}
                  />
                  <BlockLink
                    title={"Link to hear"}
                    content={data?.linkToHears}
                  />
                  <BlockLink title={"Link to CD "} content={data?.linkToBuys} />
                  {data?.perfomedAtRppf === 1 && (
                    <BlockLink title={"Rungis festival link"} content={links} />
                  )}
                </div>
              </div>

              <div className="border-2 border-gray-100 rounded-lg shadow-md hover:shadow-xl">
                <button
                  onClick={() => handleSetIsOpen(4)}
                  className="flex items-center justify-between w-full p-8"
                >
                  <h1 className="font-semibold text-gray-700 dark:text-white">
                    OTHERS INFORMATIONS
                  </h1>

                  <Status
                    onClick={() => handleSetIsOpen(4)}
                    isOpen={isOpen === 4 ? true : false}
                  />
                </button>

                <hr className="border-gray-200" />

                <div
                  id="block_4"
                  className={`${
                    isOpen === 4 ? " block " : " hidden "
                  }${" relative grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 p-4 bg-gray-100"}`}
                >
                  <Block
                    title={"First performance"}
                    content={data?.firstPerformance}
                  />
                  <Block title={"Nomenclature"} content={data?.nomenclature} />
                  {/* <Block title={"Content"} content={data?.content} /> */}
                  <Block title={"Notes"} content={data?.notes} />
                </div>
              </div>
            </div>
          </div>
        )}

        {!showDetail && (
          <div
            className={`${
              created ? " hidden " : " block "
            }${" w-full bg-white shadow-xl mb-24 w-full space-y-4 p-8"}`}
          >
            <i className="block flex flex-col items-start text-xs text-red-500">
              {" "}
              The fields with * are required{" "}
            </i>

            {/* Title */}
            <SimpleTextInput
              fieldName={"Title"}
              value={dataOeuvre.title}
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, title: e.target.value })
              }
              isSubmitted={isSubmitted}
              isRequired={true}
            />

            {/* Method */}
            <SelectInput
              value={dataOeuvre.typeOfCard}
              options={optionMethod}
              placeholder="Select method"
              fieldName={"Type of card"}
              myRef={selectMethod}
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, typeOfCard: e?.value })
              }
              isSubmitted={isSubmitted}
              isRequired={true}
            />

            {/* Publisher */}
            <SimpleTextInput
              fieldName={"Publisher"}
              value={dataOeuvre.publisher}
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, publisher: e.target.value })
              }
              isSubmitted={isSubmitted}
              isRequired={true}
            />

            {/* Type */}
            <SelectInput
              value={dataOeuvre.typeChoosen}
              fieldName={"Type"}
              options={type}
              placeholder="Select the type"
              myRef={selectType}
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, typeChoosen: e?.value })
              }
              isSubmitted={isSubmitted}
              isRequired={true}
            />

            {/* PublicationDate */}
            <InputDate
              value={dataOeuvre.dateOfPublication}
              fieldName="Date of first publication"
              onChange={(e) =>
                setDataOeuvre({
                  ...dataOeuvre,
                  dateOfPublication: e.target.value,
                })
              }
              isSubmitted={isSubmitted}
            />

            {/* Publisher reference */}
            <SimpleTextInput
              fieldName={"Publisher reference"}
              value={dataOeuvre.publisherRef}
              onClick={() => setFieldHelp("publisherRef")}
              help={fieldHelp === "publisherRef" ? true : false}
              showHelp={true}
              textHelp="Write here the reference of the score according to the publisher. For example, En Blanc et Noir published by Durand & Fils : D&F 15854"
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, publisherRef: e.target.value })
              }
              isSubmitted={isSubmitted}
            />

            {/* Display here only if methode is compilation */}
            {dataOeuvre?.typeOfCard?.toLowerCase() === "compilation" && (
              /* Arranger name */
              <SimpleTextInput
                fieldName={"Arranger name"}
                value={dataOeuvre.arrangeurName}
                onChange={(e) =>
                  setDataOeuvre({
                    ...dataOeuvre,
                    arrangeurName: e.target.value,
                  })
                }
                isSubmitted={isSubmitted}
              />
            )}

            {/* Content */}
            <TextareaInput
              fieldName={"Content"}
              value={dataOeuvre.content}
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, content: e.target.value })
              }
              isSubmitted={isSubmitted}
            />

            {/* Level */}
            <SelectInput
              value={dataOeuvre.level}
              options={optionLevel}
              placeholder="Select level"
              myRef={selectLevel}
              fieldName={"Level"}
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, level: e?.value })
              }
              isSubmitted={isSubmitted}
            />
            <>
              {/* Composer name */}
              <SelectInput
                value={dataOeuvre.composer_id}
                options={optionComposer}
                fieldName={"Composer name"}
                placeholder="Select name of composer"
                getOptionLabel={(option) =>
                  `${option.firstName + " " + option.lastName}`
                }
                getOptionValue={(option) => `${option.id}`}
                myRef={selectComposer}
                onChange={(e) =>
                  setDataOeuvre({ ...dataOeuvre, composer_id: e?.id })
                }
                isSubmitted={isSubmitted}
                isRequired={
                  dataOeuvre?.typeOfCard?.toLowerCase() === "piece"
                    ? true
                    : false
                }
              />

              {/* Collection name */}
              <SimpleTextInput
                fieldName={"Collection name"}
                value={dataOeuvre.collectionName}
                onChange={(e) =>
                  setDataOeuvre({
                    ...dataOeuvre,
                    collectionName: e.target.value,
                  })
                }
                isSubmitted={isSubmitted}
              />
            </>
            {/* Mouvement */}
            <TextareaInput
              fieldName={"Movement"}
              value={dataOeuvre.mouvement}
              onClick={() => setFieldHelp("movement")}
              help={fieldHelp === "movement" ? true : false}
              showHelp={true}
              textHelp="List here the different movements of the work, or the different pieces of a collection. Respect the following presentation:
            1.
            2.
            etc."
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, mouvement: e.target.value })
              }
              isSubmitted={isSubmitted}
            />

            {/* Date of composition */}
            <InputDate
              value={dataOeuvre.dateOfComposition}
              fieldName="Date of composition"
              onChange={(e) =>
                setDataOeuvre({
                  ...dataOeuvre,
                  dateOfComposition: e.target.value,
                })
              }
              isSubmitted={isSubmitted}
              // isRequired={true}
            />

            {/* Original or transcription */}
            <SelectInput
              fieldName={"Original or transcription"}
              placeholder="Select here..."
              myRef={selectOriginal}
              options={optionOriginal}
              value={dataOeuvre.originalTranscription}
              onChange={(e) =>
                setDataOeuvre({
                  ...dataOeuvre,
                  originalTranscription: e?.value,
                })
              }
              isSubmitted={isSubmitted}
              isRequired={true}
            />

            {dataOeuvre?.originalTranscription?.toLowerCase() ===
              "transcription" && (
              /* Transcription name */
              <SimpleTextInput
                fieldName={"Transcriptor name"}
                value={dataOeuvre.transcriptorName}
                onChange={(e) =>
                  setDataOeuvre({
                    ...dataOeuvre,
                    transcriptorName: e.target.value,
                  })
                }
                isSubmitted={isSubmitted}
              />
            )}

            {/* Instrument */}
            <SimpleTextInput
              fieldName={"Instruments"}
              value={dataOeuvre.instrument}
              onClick={() => setFieldHelp("instrument")}
              help={fieldHelp === "instrument" ? true : false}
              showHelp={true}
              textHelp="In the case of ensemble music for piano duet with other instruments, list the instruments here, separated by a comma."
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, instrument: e.target.value })
              }
              isSubmitted={isSubmitted}
            />

            {/* Duration */}
            <TimeInput
              fieldName={"Duration"}
              value={dataOeuvre.duration}
              onClick={() => setFieldHelp("duration")}
              help={fieldHelp === "duration" ? true : false}
              showHelp={true}
              textHelp="Enter here the average duration of the work"
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, duration: e.target.value })
              }
              isSubmitted={isSubmitted}
            />

            {/* First performance */}
            <SimpleTextInput
              fieldName={"First performance"}
              value={dataOeuvre.firstPerformance}
              onClick={() => setFieldHelp("firstPerformance")}
              help={fieldHelp === "firstPerformance" ? true : false}
              showHelp={true}
              textHelp="Enter here the names of the first performers and the date of creation (DD/MM/YYYY or YYYY)"
              onChange={(e) =>
                setDataOeuvre({
                  ...dataOeuvre,
                  firstPerformance: e.target.value,
                })
              }
              isSubmitted={isSubmitted}
            />

            {/* Nomenclature */}
            <SimpleTextInput
              fieldName={"Nomenclature"}
              value={dataOeuvre.nomenclature}
              onClick={() => setFieldHelp("nomenclature")}
              help={fieldHelp === "nomenclature" ? true : false}
              showHelp={true}
              textHelp="Orchestra instrumentation for a concerto. Please use the following notation type: 
            3223 / 2230 / timp.perc / str
            4(2pic)4(ca)4(bcl)3(cbn) / 7431 / 2timp.perc / hp / str
            (examples according to the usual conventions)"
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, nomenclature: e.target.value })
              }
              isSubmitted={isSubmitted}
            />

            {/* Link to score */}
            <MultipleLinkInput
              fieldName={"Link to scores"}
              onKeyUp={(event) =>
                event.key === "Enter" ? addNewLinkToScore(event) : null
              }
              onClick={() => setFieldHelp("linkToScores")}
              help={fieldHelp === "linkToScores" ? true : false}
              showHelp={true}
              showAlert={true}
              textHelp="Copy here the url to a merchant site offering the score. If the score is not or no longer published, or if it is in the public domain, copy here the url to a page allowing to download the score legally."
              isSubmitted={isSubmitted}
            >
              {Array.isArray(dataOeuvre?.linkToScores) &&
                dataOeuvre.linkToScores.map((item, index) => (
                  <li key={index} className="tag">
                    <span className="tag-title">{item}</span>
                    <span
                      className="tag-close-icon"
                      onClick={() =>
                        setDataOeuvre({
                          ...dataOeuvre,
                          linkToScores: [
                            ...dataOeuvre.linkToScores.filter(
                              (link) =>
                                dataOeuvre.linkToScores.indexOf(link) !== index
                            ),
                          ],
                        })
                      }
                    >
                      x
                    </span>
                  </li>
                ))}
            </MultipleLinkInput>

            {/* Link to hear */}
            <MultipleLinkInput
              fieldName={"Link to hear"}
              onKeyUp={(event) =>
                event.key === "Enter" ? addNewLinkToHear(event) : null
              }
              onClick={() => setFieldHelp("linkToHears")}
              help={fieldHelp === "linkToHears" ? true : false}
              showHelp={true}
              showAlert={true}
              textHelp="Copy here the url of a playlist made on a streaming platform offering one or several recorded versions of the work. Create your own playlist on the platforms you use and share them here."
              isSubmitted={isSubmitted}
            >
              {Array.isArray(dataOeuvre?.linkToHears) &&
                dataOeuvre.linkToHears?.map((item, index) => (
                  <li key={index} className="tag">
                    <span className="tag-title">{item}</span>
                    <span
                      className="tag-close-icon"
                      onClick={() =>
                        setDataOeuvre({
                          ...dataOeuvre,
                          linkToHears: [
                            ...dataOeuvre.linkToHears.filter(
                              (link) =>
                                dataOeuvre.linkToHears.indexOf(link) !== index
                            ),
                          ],
                        })
                      }
                    >
                      x
                    </span>
                  </li>
                ))}
            </MultipleLinkInput>

            {/* Link to buy */}
            <MultipleLinkInput
              fieldName={"Link to CD"}
              onKeyUp={(event) =>
                event.key === "Enter" ? addNewLinkToBuys(event) : null
              }
              onClick={() => setFieldHelp("linkToBuys")}
              help={fieldHelp === "linkToBuys" ? true : false}
              showHelp={true}
              showAlert={true}
              textHelp="Copy here the url of a site offering a commercial recording of the work on physical support"
              isSubmitted={isSubmitted}
            >
              {Array.isArray(dataOeuvre?.linkToBuys) &&
                dataOeuvre.linkToBuys?.map((item, index) => (
                  <li key={index} className="tag">
                    <span className="tag-title">{item}</span>
                    <span
                      className="tag-close-icon"
                      onClick={() =>
                        setDataOeuvre({
                          ...dataOeuvre,
                          linkToBuys: [
                            ...dataOeuvre.linkToBuys.filter(
                              (link) =>
                                dataOeuvre.linkToBuys.indexOf(link) !== index
                            ),
                          ],
                        })
                      }
                    >
                      x
                    </span>
                  </li>
                ))}
            </MultipleLinkInput>

            {/* Rungis Festival Link */}
            <RadioLink
              value={dataOeuvre.perfomedAtRppf}
              fieldName={"Performed at the Rungis Piano-Piano Festival"}
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, perfomedAtRppf: e.target.value })
              }
              isSubmitted={isSubmitted}
            />

            {/* Rungis Festival Link */}
            <TextareaInput
              fieldName={"Notes"}
              value={dataOeuvre.notes}
              onChange={(e) =>
                setDataOeuvre({ ...dataOeuvre, notes: e.target.value })
              }
              isSubmitted={isSubmitted}
            />

            <ErrorMessage errors={errors} />
            <SuccessMessage
              success={success}
              handleHideMsg={() => setSuccessMessage("")}
            />

            {/* Action */}
            <div className="flex flex-col py-4 items-end">
              <Button
                disabled={isLoading}
                onClick={() => submitFormOeuvre()}
                className={classNames(
                  !isLoading ? "" : "bg-gray-500 opacity-50 cursor-not-allowed",
                  ""
                )}
              >
                {!isLoading ? (
                  "Create"
                ) : (
                  <>
                    {" "}
                    <Loader /> <span className="mr-1"></span> Creating...{" "}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {!showDetail && (
          <div
            className={`${
              !created ? " hidden " : " block "
            }${" w-full shadow-xl overflow-x-auto"}`}
          >
            <table className="min-w-full">
              <thead className="bg-pblue text-pblue text-xs font-semibold text-left text-white uppercase">
                <tr>
                  <th scope="col" className="py-3 px-6 ">
                    Title
                  </th>
                  <th scope="col" className="py-3 px-6 ">
                    Method
                  </th>
                  <th scope="col" className="py-3 px-6">
                    Publisher
                  </th>
                  <th scope="col" className="py-3 px-6 ">
                    Type
                  </th>
                  <th scope="col" className="py-3 px-6 ">
                    Date of composition
                  </th>
                  <th scope="col">Original or transcription</th>
                  <th scope="col">Status</th>
                  <th scope="col">Details</th>
                </tr>
              </thead>
              <tbody>{displayOeuvreForUser(oeuvres)}</tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Work;
